import { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { getEvent, getEvents, eastVsWestBase, eventPath, formatDate, headFor, athleteFor, athletePath } from '../lib/events';
import { SITE_URL, getStrings, altLinks, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { CtaBlock } from '../components/CtaBlock';

function Avatar({ name, winner, lang }: { name: string; winner?: boolean; lang: Lang }) {
  const [broken, setBroken] = useState(false);
  const src = headFor(name);
  const prof = athleteFor(name);
  const initials = name.replace(/"[^"]*"/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const inner = src && !broken ? (
    <img src={src} alt={name} width={36} height={36} loading="lazy" onError={() => setBroken(true)} className="w-full h-full object-cover" />
  ) : (
    <span className="flex items-center justify-center w-full h-full text-[10px] font-bold text-faint">{initials}</span>
  );
  const cls = `relative shrink-0 w-9 h-9 rounded-full overflow-hidden bg-elevated border ${winner ? 'border-primary/50' : 'border-line'}`;
  return prof ? (
    <Link to={athletePath(lang, prof.slug)} title={name} className={`${cls} hover:opacity-90 transition`}>{inner}</Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function Fighter({ name, winner, align, lang }: { name: string; winner?: boolean; align: 'left' | 'right'; lang: Lang }) {
  const prof = athleteFor(name);
  const label = (
    <span className={`truncate ${winner ? 'text-ink font-bold' : 'text-muted'}`} title={name}>
      {winner && <span className="mr-1" aria-label="winner">🏆</span>}
      {name}
    </span>
  );
  return (
    <div className={`flex items-center gap-2 min-w-0 ${align === 'right' ? 'flex-row-reverse text-right' : ''}`}>
      <Avatar name={name} winner={winner} lang={lang} />
      {prof ? <Link to={athletePath(lang, prof.slug)} className="min-w-0 hover:text-primary transition">{label}</Link> : label}
    </div>
  );
}

export default function EventPage({ lang = 'en' as Lang }: { lang?: Lang }) {
  const { slug } = useParams();
  const t = getStrings(lang).eastVsWest;
  const base = eastVsWestBase(lang);
  const ev = slug ? getEvent(slug) : undefined;
  if (!ev) return <Navigate to={base} replace />;

  const url = `${SITE_URL}${eventPath(lang, ev.slug)}`;
  const venue = [ev.city, ev.country].filter(Boolean).join(', ');
  const headline = ev.matches[ev.matches.length - 1];
  const title = `${ev.name} — ${t.cardResultsTitle} | ArmProgress`;
  const description = headline
    ? `${ev.name} (${venue}, ${formatDate(ev.isoDate)}) full results: ${headline.winner} beat ${headline.loser} ${ev.matches.length} bouts. See every match on the card.`
    : `${ev.name} full match card and results.`;

  const eventLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: ev.name,
    sport: 'Arm wrestling',
    startDate: ev.isoDate,
    eventStatus: 'https://schema.org/EventScheduled',
    location: { '@type': 'Place', name: venue, address: venue },
    organizer: { '@type': 'Organization', name: 'East vs West' },
  };

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={title}
        description={description}
        canonical={url}
        type="article"
        lang={lang}
        jsonLd={eventLd}
        alternates={altLinks(`/east-vs-west/${ev.slug}`)}
      />
      <BlogHeader lang={lang} />

      <article className="container mx-auto px-6 py-12 max-w-3xl">
        <a href={base} className="text-faint hover:text-ink text-sm">{t.allEventsBack}</a>

        <div className="mt-4 mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-ink leading-tight mb-2">{ev.name}</h1>
          <p className="text-muted text-lg">{venue}{venue && ev.isoDate ? ' · ' : ''}{formatDate(ev.isoDate)}</p>
        </div>

        <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-3">{t.cardResultsTitle}</h2>
        <div className="rounded-2xl border border-line bg-surface/60 divide-y divide-line overflow-hidden">
          {[...ev.matches].reverse().map((m, i) => (
            <div key={i} className="flex items-center gap-2 sm:gap-3 p-4">
              <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3">
                <Fighter name={m.winner} winner align="right" lang={lang} />
                <span className="font-extrabold text-primary whitespace-nowrap text-sm sm:text-base">{m.score.replace('-', '–')}</span>
                <Fighter name={m.loser} align="left" lang={lang} />
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1 shrink-0 w-32">
                <span className="text-[11px] font-bold uppercase tracking-wide text-faint border border-line rounded-full px-2 py-0.5 whitespace-nowrap">
                  {t.arm[m.arm]}
                </span>
                {m.weightClass && <span className="text-[11px] text-faint truncate max-w-full">{m.weightClass}</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12"><CtaBlock lang={lang} /></div>
      </article>

      <Footer lang={lang} />
    </div>
  );
}

// Kept alongside the page so the archive can render tiny previews if needed.
export { getEvents };
